import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import {
  Box, Typography, Card, CardContent, Grid, Chip, CircularProgress, Alert,
  IconButton, Stack, Divider, useMediaQuery
} from '@mui/material';
import { ArrowBack, Person, LocationOn, Home, Verified } from '@mui/icons-material';
import CoordinateMapsVerifikasi from '../verifikasi/CoordinateMapsVerifikasi';

const BASE_URL = process.env.REACT_APP_URL;

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={600}>{value || '-'}</Typography>
  </Box>
);

const PhotoCard = ({ label, src }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{label}</Typography>
    {src ? (
      <Box
        component="img"
        src={src}
        alt={label}
        sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 2, border: '1px solid #e2e8f0' }}
      />
    ) : (
      <Typography variant="caption" color="text.disabled">Tidak ada</Typography>
    )}
  </Box>
);

const ExecutiveVerifikasiDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:800px)');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BASE_URL}/getverifikasi/${id}`, { withCredentials: true });
        setData(res.data?.data || res.data);
      } catch (err) {
        setError('Gagal memuat data verifikasi');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (error || !data) {
    return <Alert severity="error" sx={{ m: 2 }}>{error || 'Data tidak ditemukan'}</Alert>;
  }

  const scoreColor = data.score >= 70 ? 'success' : 'warning';

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} size="small"><ArrowBack /></IconButton>
        <Box>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700}>
            Detail Verifikasi
          </Typography>
          <Typography variant="caption" color="text.secondary">{data.namaKepalaKeluarga}</Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Score</Typography>
              <Typography variant="h4" fontWeight={700} color={`${scoreColor}.main`}>{data.score}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Kategori</Typography>
              <Chip label={data.kategori} color="primary" sx={{ mt: 0.5 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 3, textAlign: 'center', height: '100%' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Status Validasi</Typography>
              <Chip
                icon={<Verified />}
                label={data.statusVerifikasi}
                color={data.statusVerifikasi === 'Sudah Diverifikasi' ? 'success' : 'warning'}
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Person color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>Data Pribadi</Typography>
              </Stack>
              <InfoItem label="Nama Kepala Keluarga" value={data.namaKepalaKeluarga} />
              <InfoItem label="NIK" value={data.nik} />
              <InfoItem label="No. KK" value={data.nomorKK} />
              <InfoItem label="Alamat" value={data.alamat} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <LocationOn color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>Lokasi</Typography>
              </Stack>
              <InfoItem label="Kecamatan" value={data.kecamatan} />
              <InfoItem label="Desa/Kelurahan" value={data.desaKelurahan} />
              <InfoItem label="Koordinat" value={data.titikKoordinatRumah || 'Tidak tersedia'} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Peta Lokasi</Typography>
              <CoordinateMapsVerifikasi coordinates={data.titikKoordinatRumah} readOnly />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Home color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>Status Rumah & Keluarga</Typography>
              </Stack>
              <InfoItem label="Rumah Tinggal Sendiri" value={data.rumahTinggalSendiri} />
              <InfoItem label="Status Keluarga" value={data.statusKeluarga} />
              <InfoItem label="Usia Pernikahan" value={`${data.usiaPernikahan || 0} tahun`} />
              <InfoItem label="Jumlah Penghuni" value={`${data.jumlahPenghuniRumah || 0} orang`} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Status & Penghasilan</Typography>
              <InfoItem label="Status Kepemilikan Tanah" value={data.StatusKepemilikanTanah} />
              <InfoItem label="Lokasi Sesuai RTRW" value={data.apakahLokasiTanahSesuaiRTRW} />
              <InfoItem label="Tingkat Penghasilan" value={data.tingkatPenghasilanKeluarga} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Dokumentasi</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <PhotoCard label="Foto KK" src={data.fotoKK ? `${BASE_URL}/${data.fotoKK}` : null} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PhotoCard label="Foto Rumah" src={data.foto ? `${BASE_URL}/${data.foto}` : null} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PhotoCard label="Foto Surat Tanah" src={data.fotoSuratKepemilikanTanah ? `${BASE_URL}/${data.fotoSuratKepemilikanTanah}` : null} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <PhotoCard label="Foto Rumah Saat Ini" src={data.fotoRumahSaatIni ? `${BASE_URL}/${data.fotoRumahSaatIni}` : null} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Divider />
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2} sx={{ mt: 1 }}>
            <InfoItem label="Tanggal Verifikasi" value={data.tanggalVerifikasi ? moment(data.tanggalVerifikasi).format('DD MMMM YYYY') : '-'} />
            <InfoItem label="Divalidasi oleh" value={data.Admin?.username} />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExecutiveVerifikasiDetail;
